import React, { useState, useEffect } from 'react';
import './Sidebar.css'; // Import CSS for animations

const Sidebar = ({ 
    children, 
    position = 'left', 
    width = '250px',
    bg = '#f8f9fa',
    fixed = false,
    collapsible = false,
    collapsed = false,
    onToggle,
    showOnMobile = false,
    mobileBreakpoint = 768,
    shadow = true,
    border = true,
    padding = '20px',
    isMobile = false, 
    isTablet = false, 
    isDesktop = false, 
    style, 
    className,
    useMobileOffcanvas = true,
    showSidebar,
    setShowSidebar,
    ...props 
}) => {
    const [isCollapsed, setIsCollapsed] = useState(collapsed);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [isMobileView, setIsMobileView] = useState(false);

    // Detect mobile view on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobileView(window.innerWidth < mobileBreakpoint);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, [mobileBreakpoint]);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (showMobileMenu && isMobileView) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        
        return () => {
            document.body.style.overflow = '';
        };
    }, [showMobileMenu, isMobileView]);

    const handleToggle = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        if (onToggle) onToggle(newState);
        setShowSidebar(!showSidebar);
    };

    const handleMobileToggle = () => {
        setShowMobileMenu(!showMobileMenu);
    };

    const handleCloseMobile = () => {
        setShowMobileMenu(false);
    };

    const sidebarStyles = {
        position: fixed ? 'fixed' : 'relative',
        [position === 'left' ? 'left' : 'right']: 0,
        top: fixed ? 0 : 'auto',
        height: fixed ? '100vh' : 'auto',
        width: isCollapsed ? '60px' : width,
        backgroundColor: bg,
        padding: padding,
        transition: 'width 0.3s ease, transform 0.3s ease',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxShadow: shadow ? '0 0 10px rgba(0,0,0,0.1)' : 'none',
        borderRight: border && position === 'left' ? '1px solid #dee2e6' : 'none',
        borderLeft: border && position === 'right' ? '1px solid #dee2e6' : 'none',
        zIndex: 1000,
        ...style
    };

    // Mobile overlay version with custom animations
    if (useMobileOffcanvas && isMobileView) {
        return (
            <>
                {/* Hamburger Menu Button */}
                {!showMobileMenu && (
                <button
                    onClick={handleMobileToggle}
                    className="sidebar-mobile-toggle"
                    style={{
                        position: 'fixed',
                        top: '95px',
                        [position === 'left' ? 'left' : 'right']: '-6px',
                        zIndex: 1100,
                        padding: '12px 16px',
                        backgroundColor: 'rgb(255 255 255)',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        transition: 'all 0.3s ease',
                        fontSize: '20px',
                        lineHeight: 1
                    }}
                    aria-label="Toggle menu"
                >
                    {showMobileMenu ? '✕' : '>'}
                </button>
                )}
                {/* Backdrop Overlay */}
                {showMobileMenu && (
                    <div
                        className={`sidebar-backdrop ${showMobileMenu ? 'show' : ''}`}
                        onClick={handleCloseMobile}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1040,
                            animation: 'fadeIn 0.3s ease'
                        }}
                    />
                )}

                {/* Mobile Sidebar Overlay */}
                <aside
                    className={`sidebar-mobile ${showMobileMenu ? 'show' : ''} sidebar-${position}`}
                    style={{
                        position: 'fixed',
                        top: 0,
                        [position === 'left' ? 'left' : 'right']: 0,
                        height: '100vh',
                        width: width,
                        backgroundColor: bg,
                        zIndex: 1050,
                        transform: showMobileMenu 
                            ? 'translateX(0)' 
                            : position === 'left' 
                                ? 'translateX(-100%)' 
                                : 'translateX(100%)',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: showMobileMenu ? '0 8px 16px rgba(0,0,0,0.2)' : 'none',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: padding,
                        ...style
                    }}
                >
                    {/* Close Button Inside Sidebar */}
                    <button
                        onClick={handleCloseMobile}
                        style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            background: 'transparent',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: style?.color || '#333',
                            padding: '5px',
                            lineHeight: 1,
                            zIndex: 1
                        }}
                        aria-label="Close menu"
                    >
                        ✕
                    </button>

                    {/* Sidebar Content */}
                    <div style={{ marginTop: '0px' }}>
                        {children}
                    </div>
                </aside>
            </>
        );
    }

    // Desktop version
    return (
        <aside 
            className={`sidebar ${className || ''}`}
            style={sidebarStyles}
            {...props}
        >
            {collapsible && (
                <button
                    onClick={handleToggle}
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: position === 'left' ? '10px' : 'auto',
                        left: position === 'right' ? '10px' : 'auto',
                        background: 'transparent',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '5px',
                        zIndex: 1001
                    }}
                    aria-label="Toggle sidebar"
                >
                    {isCollapsed ? '☰' : '✕'}
                </button>
            )}
            
            <div style={{ 
                marginTop: collapsible ? '0px' : '0',
                opacity: isCollapsed ? 0 : 1,
                transition: 'opacity 0.3s ease'
            }}>
                {children}
            </div>
        </aside>
    );
};

export default Sidebar;

